import React, { useState, useEffect, useRef } from "react";
import { fetchCurrentUserInfo, fetchRankHistory, fetchMembersByLevel, fetchRankCountsByLevel, fetchTeamMembers, isAdminUser } from "../script/rpc";
import { useAuth } from "../App";
import { toast } from "react-toastify";

const ITEMS_PER_PAGE = 8;
const MAX_LEVELS = 7;

const fmt = (v) =>
  Number(v || 0).toLocaleString("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  });

// Injected once: handles everything inline styles can't (media queries, animations)
const RESPONSIVE_STYLES = `
@keyframes dp-spin { to { transform: rotate(360deg); } }
@keyframes dp-pulse { 0%, 100% { opacity: 0.7; } 50% { opacity: 0.3; } }

.tm-main { padding: 16px; }
.tm-level-tabs { -ms-overflow-style: none; scrollbar-width: none; }
.tm-level-tabs::-webkit-scrollbar { display: none; }
.tm-level-tab { flex: 0 0 auto; }

.tm-card-header { }
.tm-filters { }
.tm-search-wrap { }
.tm-search-input { width: 200px; }

.tm-table-wrap { overflow-x: auto; }
.tm-table { min-width: 640px; }

.tm-member-list { display: none; }

@media (max-width: 720px) {
  .tm-main { padding: 12px 12px 60px; }
  .tm-card-header {
    flex-direction: column;
    align-items: stretch !important;
  }
  .tm-filters {
    flex-direction: column;
    align-items: stretch !important;
    width: 100%;
  }
  .tm-status-group { justify-content: space-between; }
  .tm-status-group button { flex: 1; }
  .tm-search-wrap { width: 100%; }
  .tm-search-input { width: 100% !important; box-sizing: border-box; }

  .tm-level-tab { min-width: 96px !important; padding: 10px 14px !important; }

  /* Hide the table, show card list instead */
  .tm-table-wrap { display: none; }
  .tm-member-list { display: block; }

  .tm-pagination { flex-wrap: wrap; gap: 8px; }
}

@media (max-width: 420px) {
  .tm-level-tab { min-width: 84px !important; padding: 8px 10px !important; }
  .tm-level-tab span:first-child { font-size: 13px !important; }
  .tm-level-tab span:last-child { font-size: 11px !important; }
}
`;

export default function TeamMembersPage() {
  const { auth } = useAuth();
  const [activeLevel, setActiveLevel] = useState(0);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [rankHistory, setRankHistory] = useState([]);
  const [pages, setPages] = useState(Array(MAX_LEVELS).fill(1));
  const [levelData, setLevelData] = useState(
    Array(MAX_LEVELS).fill(null).map(() => ({
      members: [],
      total: 0,
      loading: false,
      fetched: false,
      ranks: [],
    }))
  );
  const [statusFilter, setStatusFilter] = useState(""); // "" (All), "active", "deactive"
  const [globallyLoading, setGloballyLoading] = useState(true);
  const rootPid = useRef(null);
  const searchTimer = useRef(null);

  // Get root partner id once and load all level counts
  useEffect(() => {
    async function init() {
      try {
        const info = await fetchCurrentUserInfo(auth.uid, auth.password);
        if (!info?.partner_id) return;
        rootPid.current = info.partner_id[0];

        // Load initial counts for all levels in parallel
        const countPromises = Array.from({ length: MAX_LEVELS }, (_, i) => {
           // We only need the total
           return fetchMembersByLevel(auth.uid, auth.password, rootPid.current, i + 1, { limit: 0 });
        });

        const results = await Promise.all(countPromises);

        setLevelData(prev => prev.map((d, i) => ({
          ...d,
          total: results[i]?.total || 0,
          fetched: true
        })));

        // Load initial data for the first level
        await loadLevel(0, 1, "");
      } catch (err) {
        console.error("Failed to load level counts", err);
      } finally {
        setGloballyLoading(false);
      }
    }
    init();
  }, [auth.uid, auth.password]);

  async function loadLevel(levelIndex, page, searchVal) {
    if (!rootPid.current) return;
    setLevelData((prev) =>
      prev.map((d, i) => (i === levelIndex ? { ...d, loading: true } : d))
    );
    try {
      const offset = (page - 1) * ITEMS_PER_PAGE;
      const isAdmin = await isAdminUser(auth.uid, auth.password);
      
      let records, total, ranks;

      if (searchVal.trim()) {
        // GLOBAL SEARCH
        // For admin, we want to search all users in the system if they want, 
        // but typically Team Members page is for "My Team".
        // Let's use fetchTeamMembers which uses treepath filter.
        // If Admin, should we search ALL? The user said "search all users". 
        // In fetchTeamMembers, if we pass null for rootPid, it will fetch all if we updated rpc.js.
        // Let's check rpc.js fetchTeamMembers.
        const searchRoot = isAdmin ? null : rootPid.current;
        const res = await fetchTeamMembers(auth.uid, auth.password, searchRoot, { offset, limit: ITEMS_PER_PAGE, search: searchVal });
        records = res.records;
        total = res.total;
        ranks = []; // Skip rank breakdown for global search for now
      } else {
        // LEVEL-BY-LEVEL VIEW
        const res = await fetchMembersByLevel(
          auth.uid,
          auth.password,
          rootPid.current,
          levelIndex + 1,
          { offset, limit: ITEMS_PER_PAGE, search: "", status: statusFilter }
        );
        records = res.records;
        total = res.total;
        ranks = await fetchRankCountsByLevel(
          auth.uid,
          auth.password,
          rootPid.current,
          levelIndex + 1,
          { search: "", status: statusFilter }
        );
      }

      setLevelData((prev) =>
        prev.map((d, i) =>
          i === levelIndex
            ? { members: records, total, ranks: ranks || [], loading: false, fetched: true }
            : d
        )
      );
    } catch (err) {
      console.error("Load failed:", err);
      setLevelData((prev) =>
        prev.map((d, i) =>
          i === levelIndex
            ? { ...d, loading: false, fetched: true }
            : d
        )
      );
    }
  }

  const handleTab = (i) => {
    setActiveLevel(i);
    setSearchInput("");
    setSearch("");
    const newPage = 1;
    setPages((prev) => {
      const n = [...prev];
      n[i] = newPage;
      return n;
    });
    loadLevel(i, newPage, "");
  };

  const handleSearch = (val) => {
    setSearchInput(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setSearch(val);
      setPages((prev) => {
        const n = [...prev];
        n[activeLevel] = 1;
        return n;
      });
      loadLevel(activeLevel, 1, val);
    }, 350);
  };

  const handlePageChange = (p) => {
    setPages((prev) => {
      const n = [...prev];
      n[activeLevel] = p;
      return n;
    });
    loadLevel(activeLevel, p, search);
  };

  // Re-load when status filter changes
  useEffect(() => {
    if (rootPid.current) {
      setPages((prev) => {
        const n = [...prev];
        n[activeLevel] = 1;
        return n;
      });
      loadLevel(activeLevel, 1, search);
    }
  }, [statusFilter]);

  const { members, total, loading, ranks } = levelData[activeLevel];
  const page = pages[activeLevel];
  const totalPages = Math.ceil(total / ITEMS_PER_PAGE);

  const getPgNums = () => {
    const nums = [];
    for (let p = 1; p <= totalPages; p++) {
      if (p === 1 || p === totalPages || Math.abs(p - page) <= 1) {
        nums.push(p);
      } else if (nums[nums.length - 1] !== "…") {
        nums.push("…");
      }
    }
    return nums;
  };

  return (
    <main className="dp-main tm-main" style={{ position: "relative" }}>
      <style>{RESPONSIVE_STYLES}</style>

      {/* Page Header */}
      <div style={{ marginBottom: 20 }}>
        <h1
          style={{
            fontSize: 22,
            fontWeight: 800,
            color: "#0f172a",
            margin: 0,
            letterSpacing: "-0.3px",
          }}
        >
          Team Members
        </h1>
        <p style={{ margin: "4px 0 0", color: "#64748b", fontSize: 13 }}>
          View your Quantobuzz across all levels
        </p>
      </div>

      {/* Level Tabs */}
      <div
        className="tm-level-tabs"
        style={{
          display: "flex",
          gap: 10,
          marginBottom: 16,
          overflowX: "auto",
          paddingBottom: 4,
        }}
      >
        {Array.from({ length: MAX_LEVELS }, (_, i) => {
          const isActive = activeLevel === i;
          const fetched = levelData[i].fetched;
          const count = levelData[i].total;

          return (
            <button
              key={i}
              className="tm-level-tab"
              onClick={() => handleTab(i)}
              style={{
                padding: "12px 20px",
                minWidth: "120px",
                borderRadius: "12px",
                border: isActive
                  ? "2px solid #7c3aed"
                  : "1px solid #e5e7eb",
                background: isActive ? "#7c3aedad" : "#ffffff",
                color: isActive ? "#ffffff" : "#374151",
                cursor: "pointer",
                transition: "all 0.2s ease",
                boxShadow: isActive
                  ? "0 4px 12px rgba(124,58,237,0.25)"
                  : "0 1px 3px rgba(0,0,0,0.08)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "4px",
              }}
            >
              <span
                style={{
                  fontWeight: 600,
                  fontSize: "14px",
                }}
              >
                Level {i + 1}
              </span>

              <span
                style={{
                  fontSize: "12px",
                  opacity: isActive ? 0.9 : 0.7,
                  animation: !fetched ? "dp-pulse 1.5s infinite" : "none"
                }}
              >
                {fetched ? `${count} Members` : "Calculating..."}
              </span>
            </button>
          );
        })}
      </div>

      <div
        style={{
          background: "#fff",
          border: "1px solid #e2e8f0",
          borderRadius: 16,
          overflow: "hidden",
          position: "relative", // Needed for absolute loader
          minHeight: 300,        // Ensure space for loader
        }}
      >
        {/* Local Loader overlay (Like in Dashboard) */}
        <div style={{
          position: "absolute", inset: 0, zIndex: 50,
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          background: "rgba(255, 255, 255, 0.7)", backdropFilter: "blur(4px)",
          opacity: globallyLoading ? 1 : 0, pointerEvents: globallyLoading ? "auto" : "none",
          transition: "opacity 0.3s ease"
        }}>
          <div style={{ width: 40, height: 40, borderRadius: "50%", border: "3px solid #f1f5f9", borderTopColor: "#7c3aed", animation: "dp-spin 0.8s linear infinite" }} />
          <p style={{ marginTop: 12, fontSize: 13, fontWeight: 600, color: "#64748b" }}>Loading network data...</p>
        </div>
        {/* Card Header */}
        <div
          className="tm-card-header"
          style={{
            padding: "16px 24px",
            borderBottom: "1px solid #f1f5f9",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: "#6366f1",
                display: "inline-block",
              }}
            />
            <span style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>
              {search ? "Global Search Results" : `Level ${activeLevel + 1} members`}
            </span>
            <span style={{ fontSize: 13, color: "#94a3b8" }}>
              {total} {search ? "matches" : "members"}
            </span>
          </div>
          {/* Status Filters & Search */}
          <div className="tm-filters" style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <div className="tm-status-group" style={{ display: "flex", background: "#f1f5f9", padding: 4, borderRadius: 10, gap: 4 }}>
              {[
                { label: "All", value: "" },
                { label: "Active", value: "active" },
                { label: "Inactive", value: "deactive" },
              ].map((btn) => (
                <button
                  key={btn.label}
                  onClick={() => setStatusFilter(btn.value)}
                  style={{
                    padding: "6px 16px",
                    fontSize: 12,
                    fontWeight: 700,
                    borderRadius: 8,
                    border: "none",
                    cursor: "pointer",
                    background: statusFilter === btn.value ? "#fff" : "transparent",
                    color: statusFilter === btn.value ? "#6366f1" : "#64748b",
                    boxShadow: statusFilter === btn.value ? "0 2px 4px rgba(0,0,0,0.05)" : "none",
                    transition: "all 0.2s"
                  }}
                >
                  {btn.label}
                </button>
              ))}
            </div>

            <div className="tm-search-wrap" style={{ position: "relative" }}>
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#94a3b8"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{
                  position: "absolute",
                  left: 10,
                  top: "50%",
                  transform: "translateY(-50%)",
                  pointerEvents: "none",
                }}
              >
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                type="text"
                className="tm-search-input"
                placeholder="Search name or node ID..."
                value={searchInput}
                onChange={(e) => handleSearch(e.target.value)}
                style={{
                  padding: "8px 14px 8px 32px",
                  fontSize: 13,
                  border: "1px solid #e2e8f0",
                  borderRadius: 8,
                  background: "#f8fafc",
                  color: "#0f172a",
                  outline: "none",
                }}
              />
            </div>
          </div>
        </div>

        {/* Rank Distribution Row */}
        {!loading && ranks.length > 0 && (
          <div style={{
            padding: "10px 24px",
            background: "#f8fafc",
            borderBottom: "1px solid #f1f5f9",
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap"
          }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.5px" }}>Rank Breakdown:</span>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              {ranks.map((r, i) => (
                <div key={i} style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  background: "#fff",
                  border: "1px solid #e2e8f0",
                  padding: "4px 10px",
                  borderRadius: 8,
                  boxShadow: "0 1px 2px rgba(0,0,0,0.03)"
                }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: "#1e293b" }}>{r.rank}</span>
                  <span style={{
                    fontSize: 10,
                    fontWeight: 800,
                    background: "#7c3aed",
                    color: "#fff",
                    padding: "1px 6px",
                    borderRadius: 5,
                    minWidth: 18,
                    textAlign: "center"
                  }}>{r.count}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Loading / Empty states (shared by table + card view) */}
        {loading ? (
          <div style={{ padding: "60px 0", textAlign: "center", color: "#94a3b8" }}>
            <div
              style={{
                width: 28,
                height: 28,
                border: "2.5px solid #e2e8f0",
                borderTopColor: "#6366f1",
                borderRadius: "50%",
                animation: "dp-spin 0.7s linear infinite",
                margin: "0 auto 10px",
              }}
            />
            <p style={{ fontSize: 13 }}>Loading members...</p>
          </div>
        ) : !members.length ? (
          <div style={{ padding: "60px 24px", textAlign: "center", color: "#94a3b8" }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>👥</div>
            <p style={{ fontSize: 14 }}>
              {search ? "No members match your search" : "No members at this level"}
            </p>
          </div>
        ) : (
          <>
            {/* Table - desktop/tablet view, hidden on mobile via CSS */}
            <div className="tm-table-wrap">
              <table className="tm-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: "1px solid #f1f5f9" }}>
                    {[
                      { label: "Member", align: "left" },
                      { label: "Node ID", align: "left" },
                      { label: "Rank", align: "left" },
                      { label: "Total Commission", align: "right" },
                      { label: "Total Amount", align: "right" },
                      { label: "Status", align: "center" },
                    ].map((col) => (
                      <th
                        key={col.label}
                        style={{
                          padding: "10px 20px",
                          fontSize: 11,
                          fontWeight: 600,
                          color: "#94a3b8",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          textAlign: col.align,
                          background: "#fafafa",
                        }}
                      >
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {members.map((m, idx) => (
                    <tr
                      key={m.id}
                      style={{
                        borderBottom:
                          idx < members.length - 1 ? "1px solid #f8fafc" : "none",
                      }}
                    >
                      <td style={{ padding: "14px 20px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <div
                            style={{
                              width: 32,
                              height: 32,
                              borderRadius: "50%",
                              background: "#eff6ff",
                              color: "#3b82f6",
                              fontWeight: 700,
                              fontSize: 13,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            {m.name?.[0]?.toUpperCase() || "?"}
                          </div>
                          <span style={{ fontWeight: 600, fontSize: 13, color: "#0f172a" }}>
                            {m.name || "Unnamed"}
                          </span>
                        </div>
                      </td>
                      <td style={{ padding: "14px 20px" }}>
                        <span
                          style={{
                            fontSize: 12,
                            fontWeight: 600,
                            color: "#3b82f6",
                            background: "#eff6ff",
                            padding: "3px 10px",
                            borderRadius: 6,
                          }}
                        >
                          {m.mlm_sequence || "—"}
                        </span>
                      </td>
                      <td style={{ padding: "14px 20px", color: "#475569", fontSize: 13 }}>
                        {m.post_id?.[1] || "Member"}
                      </td>
                      <td
                        style={{
                          padding: "14px 20px",
                          textAlign: "right",
                          fontWeight: 700,
                          fontSize: 13,
                          color: "#0f172a",
                        }}
                      >
                        {fmt(m.total_commission)}
                      </td>
                      <td
                        style={{
                          padding: "14px 20px",
                          textAlign: "right",
                          fontWeight: 700,
                          fontSize: 13,
                          color: "#0f172a",
                        }}
                      >
                        {fmt(m.total_amount)}
                      </td>
                      <td style={{ padding: "14px 20px", textAlign: "center" }}>
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 5,
                            fontSize: 12,
                            fontWeight: 600,
                            padding: "4px 10px",
                            borderRadius: 20,
                            background: m.status === "active" ? "#f0fdf4" : "#fff1f2",
                            color: m.status === "active" ? "#16a34a" : "#e11d48",
                          }}
                        >
                          <span
                            style={{
                              width: 6,
                              height: 6,
                              borderRadius: "50%",
                              background: m.status === "active" ? "#22c55e" : "#f43f5e",
                              flexShrink: 0,
                            }}
                          />
                          {m.status === "active" ? "Active" : "Inactive"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Card list - mobile view, hidden on desktop via CSS */}
            <div className="tm-member-list">
              {members.map((m, idx) => (
                <div
                  key={m.id}
                  style={{
                    padding: "14px 16px",
                    borderBottom:
                      idx < members.length - 1 ? "1px solid #f1f5f9" : "none",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                    <div
                      style={{
                        width: 34,
                        height: 34,
                        borderRadius: "50%",
                        background: "#eff6ff",
                        color: "#3b82f6",
                        fontWeight: 700,
                        fontSize: 14,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      {m.name?.[0]?.toUpperCase() || "?"}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 14, color: "#0f172a" }}>
                        {m.name || "Unnamed"}
                      </div>
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 600,
                          color: "#3b82f6",
                          background: "#eff6ff",
                          padding: "2px 8px",
                          borderRadius: 6,
                          display: "inline-block",
                          marginTop: 3,
                        }}
                      >
                        {m.mlm_sequence || "—"}
                      </span>
                    </div>
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 5,
                        fontSize: 11,
                        fontWeight: 600,
                        padding: "4px 9px",
                        borderRadius: 20,
                        background: m.status === "active" ? "#f0fdf4" : "#fff1f2",
                        color: m.status === "active" ? "#16a34a" : "#e11d48",
                        flexShrink: 0,
                      }}
                    >
                      <span
                        style={{
                          width: 6,
                          height: 6,
                          borderRadius: "50%",
                          background: m.status === "active" ? "#22c55e" : "#f43f5e",
                          flexShrink: 0,
                        }}
                      />
                      {m.status === "active" ? "Active" : "Inactive"}
                    </span>
                  </div>

                  <div style={{ display: "flex", gap: 8, fontSize: 12, color: "#64748b", marginBottom: 8 }}>
                    <span style={{ fontWeight: 600 }}>Rank:</span>
                    <span style={{ color: "#475569" }}>{m.post_id?.[1] || "Member"}</span>
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 8,
                      background: "#f8fafc",
                      borderRadius: 10,
                      padding: "8px 10px",
                    }}
                  >
                    <div>
                      <div style={{ fontSize: 10, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.5px", fontWeight: 700 }}>
                        Commission
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>
                        {fmt(m.total_commission)}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: 10, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.5px", fontWeight: 700 }}>
                        Total Amount
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>
                        {fmt(m.total_amount)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Pagination */}
        {total > ITEMS_PER_PAGE && (
          <div
            className="tm-pagination"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "12px 20px",
              borderTop: "1px solid #f1f5f9",
              background: "#fafafa",
            }}
          >
            <span style={{ fontSize: 13, color: "#64748b" }}>
              {(page - 1) * ITEMS_PER_PAGE + 1}–
              {Math.min(page * ITEMS_PER_PAGE, total)} of {total}
            </span>
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <button
                onClick={() => handlePageChange(page - 1)}
                disabled={page <= 1}
                style={{
                  width: 32, height: 32,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  border: "1px solid #e2e8f0", borderRadius: 8,
                  background: page <= 1 ? "#f8fafc" : "#fff",
                  color: page <= 1 ? "#cbd5e1" : "#374151",
                  cursor: page <= 1 ? "not-allowed" : "pointer",
                  fontSize: 16, fontWeight: 500,
                }}
              >
                ‹
              </button>
              {getPgNums().map((p, i) =>
                p === "…" ? (
                  <span
                    key={`e${i}`}
                    style={{
                      width: 32, height: 32,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 13, color: "#94a3b8",
                    }}
                  >
                    …
                  </span>
                ) : (
                  <button
                    key={p}
                    onClick={() => handlePageChange(p)}
                    style={{
                      width: 32, height: 32,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      border: p === page ? "1.5px solid #6366f1" : "1px solid #e2e8f0",
                      borderRadius: 8,
                      background: p === page ? "#6366f1" : "#fff",
                      color: p === page ? "#fff" : "#374151",
                      cursor: "pointer",
                      fontSize: 13,
                      fontWeight: p === page ? 700 : 500,
                    }}
                  >
                    {p}
                  </button>
                )
              )}
              <button
                onClick={() => handlePageChange(page + 1)}
                disabled={page >= totalPages}
                style={{
                  width: 32, height: 32,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  border: "1px solid #e2e8f0", borderRadius: 8,
                  background: page >= totalPages ? "#f8fafc" : "#fff",
                  color: page >= totalPages ? "#cbd5e1" : "#374151",
                  cursor: page >= totalPages ? "not-allowed" : "pointer",
                  fontSize: 16, fontWeight: 500,
                }}
              >
                ›
              </button>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}